import React, { useState, useEffect, useMemo, useCallback } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { Modal, Button, Switch } from "@mui/material";
import { makeStyles } from '@mui/styles';
import { Alert } from "reactstrap";
import spinner from "../../../assets/images/loadingGif.gif";

const useStyles = makeStyles((theme) => ({
  appBar: {
    backgroundColor: "whitesmoke",
  },
  activeButton: {
    color: "white",
    marginLeft: "auto",
    textDecoration: "none",
  },
  logo: {
    maxWidth: 160,
  },
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
    border: "2px solid #000",
    boxShadow: theme.shadows[5],
    padding: theme.spacing(2, 4, 3),
    overflowY: "auto",
    maxHeight: "100vh",
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120,
  },
  buttonContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
  },
}));

export const ComplianceAddOnsModal = (props) => {
  console.log("ComplianceAddOnsModal props: " , props);
  const classes = useStyles();
  const [addonStatus, setAddonStatus] = useState({});
  const [hasChanges, setHasChanges] = useState(false); // New state to track changes
  
  const addons = useMemo(() => props.addons || [], [props.addons]);

  const handleModalClose = useCallback(() => props.setOpen(false), []);

  // Initialize addonStatus based on the props.addons when the component mounts
  useEffect(() => {
    const initialStatus = addons.reduce((acc, addon) => {
      acc[addon.name] = addon.enabled || false; // Reflect the enabled status
      return acc;
    }, {});
    setAddonStatus(initialStatus);
  }, [addons]);

  // Check if there are changes between the current state and the initial props.addons
  useEffect(() => {
    const changesExist = addons.some(
      (addon) => addon.enabled !== addonStatus[addon.name]
    );
    setHasChanges(changesExist);
  }, [addonStatus, addons]);

  const handleSwitchChange = (name) => (event) => {
    setAddonStatus({
      ...addonStatus,
      [name]: event.target.checked,
    });
  };

  let updateOrgAccountMsg = props.updateOrgAccountMessage;
  useEffect(() => {
    if (updateOrgAccountMsg) {
      handleModalClose();
    }
  }, [updateOrgAccountMsg, handleModalClose]);

  const handleUpdateSubscription = () => {
    const updatedAddons = addons.map((addon) => ({
      name: addon.name,
      enabled: addonStatus[addon.name],
    }));
    console.log("Update COmpliance :: ", props)
    const updateOrgAccountPayload = {
      acccode: props?.orgAccountCode,
      subscription: props?.subscription,
      expiration_date: props?.expiration_date,
      complianceAddOns: updatedAddons,
      resourceGroupRequestDTOS: props.resourceGroup,
      addOns: props?.additionalAddons?.map((addOn) => ({
        name: addOn?.name,
        enabled: addOn?.enabled,
        curEstimatesNumDays: addOn?.curEstimatesNumDays
      })),
      subscription_type: props?.subscription_type,
      marketplaceDetailsDTO: {
        offerId: props.account?.marketplaceDetails?.offerId || "",
        agreementId: props.account?.marketplaceDetails?.agreementId || ""
      },
      scan_frequency: ""+ props.account?.scan_frequency,
      contract_amount: props.account?.contract_amount,
      threshold_amount: props.account?.threshold_amount,
      usage_percentage: props.account?.usage_percentage,
      islocked: props.account?.islocked || false,
      payment_term: props.account?.payment_term || "N/A",
    };

    console.log("updateOrgAccountPayload : ", updateOrgAccountPayload);
    props.updateOrgAccount(updateOrgAccountPayload);
  };

  const columns = [
    { field: "name", headerName: "AddOns", width: 100 },
    { field: "description", headerName: "Description", width: 200 },
    {
      field: "enabled",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Switch
          checked={addonStatus[params.row.name] || false}
          onChange={handleSwitchChange(params.row.name)}
          color="primary"
        />
      ),
    },
    {
      field: "price",
      headerName: "Price",
      width: 100,
      renderCell: (params) => "$" + params.row.price,
    },
  ];

  return (
    <Modal
      className={classes.modal}
      open={props.isOpen}
      onClose={handleModalClose}
    >
      <div className={classes.paper}>
        <h3>List of Compliance AddOns for Organization Account</h3>
        {props.isUpdatingOrgAccount && (
          <div
            style={{
              display: "flex",
              direction: "column",
              alignContent: "center",
              alignItems: "center",
              justifyContent: "center",
              justifyItems: "center",
            }}
          >
            <img src={spinner} width="32px" alt="Spinner" />
          </div>
        )}
        {props.updateOrgAccountError && (
          <Alert className="mt-3 mb-0" color="danger">
            Error while updating organization account.
            {JSON.stringify(props.updateOrgAccountError?.message)}
          </Alert>
        )}
        <DataGrid
          rows={addons}
          columns={columns}
          pageSize={5}
          components={{
            Toolbar: GridToolbar,
          }}
          disableSelectionOnClick
          autoHeight
          disableColumnMenu
          disableColumnSelector
          getRowId={(row) => row.name}
        />
        <div className={classes.buttonContainer}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleUpdateSubscription}
            disabled={props.isUpdatingOrgAccount || !hasChanges} // Disable if no changes
          >
            Submit
          </Button>
        </div>
      </div>
    </Modal>
  );
};
